import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { agents } from '../../../../../../api/agents'
import { usePipelineForm } from './PipelineFormContext'
import { usePipelineWorkspace } from './PipelineWorkspaceContext'
import stableStringify from './stableStringify'

const PipelineChatContext = createContext({})

export const usePipelineChat = () => useContext(PipelineChatContext)

function PipelineChatProvider ({ children }) {
  const { code } = usePipelineForm()
  const { pipelineJson, variables } = usePipelineWorkspace()

  const [threadId, setThreadId] = useState(null)
  const [localAgentStatus, setLocalAgentStatus] = useState(null)

  const lastSentPipelineRef = useRef(null)
  const lastSentVariablesRef = useRef(null)
  const lastSentCodeRef = useRef(null)

  const { mutateAsync: invokeAgentMutation } = agents.useInvokeAgent()
  const { data: threadData } = agents.useAgentThread(threadId, {
    enabled: !!threadId
  })

  const messages = useMemo(() => threadData?.messages || [], [threadData?.messages])
  const errorMessage = useMemo(() => threadData?.errorMessage || null, [threadData?.errorMessage])

  const agentStatus = useMemo(() => {
    if (localAgentStatus) return localAgentStatus
    if (!threadData) return 'idle'
    if (threadData.status === 'done') return 'done'
    if (threadData.status === 'pending' || threadData.status === 'running') return 'processing'
    return 'idle'
  }, [threadData, localAgentStatus])

  const buildAttachments = useCallback(() => {
    const pipelineChanged = pipelineJson !== lastSentPipelineRef.current
    const variablesChanged = stableStringify(variables) !== stableStringify(lastSentVariablesRef.current)
    const codeChanged = code !== lastSentCodeRef.current

    if (!pipelineChanged && !variablesChanged && !codeChanged) return []

    const attachments = []
    try {
      const parsed = JSON.parse(pipelineJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        attachments.push({
          name: 'pipeline',
          content: parsed,
          ...(code ? { metadata: { pipelineCode: code } } : {})
        })
      }
    } catch {
      // Skip invalid JSON
    }

    const testInputs = { filters: {}, dates: {} }
    if (variables?.filters) testInputs.filters = variables.filters
    if (variables?.dates) testInputs.dates = variables.dates

    if (Object.keys(testInputs.filters).length > 0 || Object.keys(testInputs.dates).length > 0) {
      attachments.push({
        name: 'testInputs',
        content: testInputs
      })
    }

    return attachments
  }, [pipelineJson, variables, code])

  const invokeAgent = useCallback(async (message, attachments = []) => {
    try {
      setLocalAgentStatus('processing')
      // Update refs synchronously before mutation to prevent race conditions
      // if buildAttachments is called again before this completes
      lastSentPipelineRef.current = pipelineJson
      lastSentVariablesRef.current = variables
      lastSentCodeRef.current = code
      const result = await invokeAgentMutation({
        message,
        threadId: threadId || undefined,
        attachments
      })
      if (result.threadId) {
        setThreadId(result.threadId)
        setLocalAgentStatus(null)
      }
      return result
    } catch (error) {
      setLocalAgentStatus('error')
      throw error
    }
  }, [threadId, invokeAgentMutation, pipelineJson, variables, code])

  const setAgentStatus = useCallback((status) => {
    setLocalAgentStatus(status)
  }, [])

  const value = useMemo(() => ({
    threadId,
    threadData,
    messages,
    agentStatus,
    setAgentStatus,
    invokeAgent,
    buildAttachments,
    errorMessage
  }), [
    threadId, threadData,
    messages, agentStatus,
    invokeAgent, buildAttachments,
    errorMessage, setAgentStatus
  ])

  return (
    <PipelineChatContext.Provider value={value}>
      {children}
    </PipelineChatContext.Provider>
  )
}

PipelineChatProvider.propTypes = {
  children: PropTypes.node
}

export default PipelineChatProvider
